import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import ScoutBox from "@/components/ScoutBox";
import { NewsletterOptIn } from "@/components/NewsletterOptIn";
import { trackEvent } from "@/lib/analytics";

const SPORTS = ["General", "Soccer", "Basketball", "Baseball", "Football", "Volleyball"];

export default function PracticePlan() {
  const [form, setForm] = useState({ email: "", name: "", players: 12, minutes: 60, equipment: "Cones, balls, pinnies", sport: "General", newsletter_opt_in: false });
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Add an email so we can save this plan."); return; }
    setBusy(true);
    setPlan(null);
    try {
      const { data } = await api.post("/practice-plan", {
        email: form.email, name: form.name || null,
        players: Number(form.players), minutes: Number(form.minutes),
        equipment: form.equipment, sport: form.sport, newsletter_opt_in: form.newsletter_opt_in,
      });
      if (form.newsletter_opt_in) trackEvent("newsletter_signup", { source: "practice_plan" });
      setPlan(data.plan);
      toast.success("Scout built your plan.");
    } catch {
      toast.error("Couldn't build that plan. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-12" data-testid="practice-plan-page">
      <Seo title="Practice Plan Generator" description="Build a full, timed youth-sports practice plan in seconds — free tool from Voluntold." path="/tools/practice-plan" />
      <span className="kicker">Free Tool</span>
      <h1 className="mt-2 font-head font-semibold uppercase text-3xl lg:text-5xl text-[#1E1E1E] leading-[1.05]">Practice Plan Generator</h1>
      <p className="mt-3 text-[#6E6E6E]">Tell Scout what you're working with. Get a real, timed session back — no more "okay… what now?"</p>

      <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="kicker">Players</label>
          <input type="number" min="1" max="60" value={form.players} onChange={(e) => setForm({ ...form, players: e.target.value })}
            data-testid="pp-players-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" />
        </div>
        <div>
          <label className="kicker">Time available (min)</label>
          <input type="number" min="15" max="180" value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })}
            data-testid="pp-minutes-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" />
        </div>
        <div className="sm:col-span-2">
          <label className="kicker">Sport</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {SPORTS.map((s) => (
              <button type="button" key={s} onClick={() => setForm({ ...form, sport: s })} data-testid={`pp-sport-${s.toLowerCase()}`}
                className={`px-4 py-2 border-2 font-head uppercase tracking-wide text-xs transition-colors ${form.sport === s ? "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]" : "border-[#D3CFC0] bg-[#E7E3D9] text-[#6E6E6E] hover:border-[#2D2D2D]"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="kicker">Equipment on hand</label>
          <input value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })}
            data-testid="pp-equipment-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" />
        </div>
        <div>
          <label className="kicker">Name (optional)</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="pp-name-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" />
        </div>
        <div>
          <label className="kicker">Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="pp-email-input" className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="you@email.com" />
        </div>
        <NewsletterOptIn className="sm:col-span-2" checked={form.newsletter_opt_in} onChange={(v) => setForm({ ...form, newsletter_opt_in: v })} testId="pp-newsletter-optin" />
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} data-testid="pp-submit-btn"
            className="w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
            {busy ? "Scout is drawing it up…" : "Build my practice plan"}
          </button>
        </div>
      </form>

      {plan && (
        <div className="mt-12 rise" data-testid="pp-result">
          <div className="border-2 border-[#1E1E1E] bg-[#E7E3D9]">
            <div className="p-6 border-b-2 border-[#D3CFC0]">
              <h2 className="font-head font-semibold uppercase text-2xl text-[#1E1E1E]">{plan.title}</h2>
              <p className="mt-2 text-[#6E6E6E] text-[15px]">{plan.summary}</p>
            </div>
            <div className="divide-y divide-[#D3CFC0]">
              {plan.blocks.map((b, i) => (
                <div key={i} className="p-6 flex gap-5" data-testid={`pp-block-${i}`}>
                  <div className="shrink-0 w-16 text-center">
                    <div className="font-head text-3xl text-[#E8A33D]">{b.minutes}</div>
                    <div className="text-[10px] text-[#6E6E6E] uppercase tracking-wide">min</div>
                  </div>
                  <div>
                    <h3 className="font-head uppercase text-lg text-[#1E1E1E]">{b.name}</h3>
                    <p className="mt-1 text-sm text-[#6E6E6E]"><strong className="text-[#1E1E1E]">Setup:</strong> {b.setup}</p>
                    <p className="mt-1 text-sm text-[#6E6E6E]"><strong className="text-[#1E1E1E]">Coaching point:</strong> {b.coaching_point}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6"><ScoutBox testId="pp-scout-note">{plan.scout_note}</ScoutBox></div>
        </div>
      )}
    </div>
  );
}
